import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {MediaOfflineService} from '../../core/services/media-offline.service';

export interface ImagePalette {
  top: string;
  bottom: string;
  /** Display-only rendition without the matte baked into older cover uploads. */
  contentUrl?: string;
}

@Injectable({providedIn: 'root'})
export class ImagePaletteService {
  private readonly cache = new Map<string, Promise<ImagePalette | null>>();
  private readonly cacheLimit = 32;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly offlineMedia: MediaOfflineService,
  ) {}

  public getPalette(url: string): Promise<ImagePalette | null> {
    if (!url || !isPlatformBrowser(this.platformId)) return Promise.resolve(null);
    const cached = this.cache.get(url);
    if (cached) {
      this.cache.delete(url);
      this.cache.set(url, cached);
      return cached;
    }

    const palette = this.loadPalette(url).catch(() => null);
    this.cache.set(url, palette);
    if (this.cache.size > this.cacheLimit) this.cache.delete(this.cache.keys().next().value);
    return palette;
  }

  private async loadPalette(url: string): Promise<ImagePalette | null> {
    const source = await this.offlineMedia.resolveUrl(url, 'image');
    return new Promise(resolve => {
      const image = this.document.createElement('img');
      // Anonymous CORS allows pixel reads from the media host without credentials.
      image.crossOrigin = 'anonymous';
      const finish = (palette: ImagePalette | null) => {
        image.onload = null;
        image.onerror = null;
        resolve(palette);
      };
      image.onerror = () => finish(null);
      image.onload = () => {
        try {
          const canvas = this.document.createElement('canvas');
          canvas.width = canvas.height = 32;
          const context = canvas.getContext('2d', {willReadFrequently: true});
          if (!context) return finish(null);
          const content = this.withoutLegacyPadding(image);
          context.drawImage(content || image, 0, 0, 32, 32);
          const pixels = context.getImageData(0, 0, 32, 32).data;
          const top = this.averageColor(pixels, 0, 32 * 16 * 4);
          const bottom = this.averageColor(pixels, 32 * 16 * 4, pixels.length);
          finish(top && bottom ? {top, bottom, contentUrl: content?.toDataURL('image/png')} : null);
        } catch {
          // CORS or a failed canvas read must never affect the visible image.
          finish(null);
        }
      };
      image.src = source;
    });
  }

  private withoutLegacyPadding(image: HTMLImageElement): HTMLCanvasElement | null {
    const scale = Math.min(1, 1024 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = this.document.createElement('canvas');
    const width = canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d', {willReadFrequently: true});
    if (!context) return null;
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;
    // The old cover cropper exported symmetric, opaque #1e1e1e bars.
    // Require complete uniform edge strips; dark areas within a photo are kept.
    const isMatte = (x: number, y: number) => {
      const offset = (y * width + x) * 4;
      return pixels[offset + 3] >= 250
        && Math.abs(pixels[offset] - 30) <= 3
        && Math.abs(pixels[offset + 1] - 30) <= 3
        && Math.abs(pixels[offset + 2] - 30) <= 3;
    };
    const columnIsMatte = (x: number) => {
      for (let y = 0; y < height; y++) if (!isMatte(x, y)) return false;
      return true;
    };
    const rowIsMatte = (y: number) => {
      for (let x = 0; x < width; x++) if (!isMatte(x, y)) return false;
      return true;
    };
    let left = 0, right = 0, top = 0, bottom = 0;
    while (left < width / 2 && columnIsMatte(left)) left++;
    while (right < width / 2 && columnIsMatte(width - right - 1)) right++;
    while (top < height / 2 && rowIsMatte(top)) top++;
    while (bottom < height / 2 && rowIsMatte(height - bottom - 1)) bottom++;
    if (Math.min(left, right) < width * .06 || Math.abs(left - right) > width * .02) left = right = 0;
    if (Math.min(top, bottom) < height * .06 || Math.abs(top - bottom) > height * .02) top = bottom = 0;
    const contentWidth = width - left - right;
    const contentHeight = height - top - bottom;
    if (!(left || right || top || bottom) || contentWidth < 16 || contentHeight < 16) return null;
    const content = this.document.createElement('canvas');
    content.width = contentWidth;
    content.height = contentHeight;
    const contentContext = content.getContext('2d');
    if (!contentContext) return null;
    contentContext.drawImage(canvas, left, top, contentWidth, contentHeight, 0, 0, contentWidth, contentHeight);
    return content;
  }

  private averageColor(pixels: Uint8ClampedArray, start: number, end: number): string | null {
    let red = 0, green = 0, blue = 0, weight = 0;
    for (let offset = start; offset < end; offset += 4) {
      const alpha = pixels[offset + 3] / 255;
      if (!alpha) continue;
      red += pixels[offset] * alpha;
      green += pixels[offset + 1] * alpha;
      blue += pixels[offset + 2] * alpha;
      weight += alpha;
    }
    return weight ? `rgb(${Math.round(red / weight)}, ${Math.round(green / weight)}, ${Math.round(blue / weight)})` : null;
  }
}
